<template>
  <div class="calendar-filter-bar">
    <div class="calendar-filter-bar__filters-wrapper">
      <!-- Projects & Mailboxes -->
      <dropdown title="Projects & Mailboxes" popper-min-width-size="large">
        <base-ifp-filter
          label="Projects / Mailboxes"
          clearable
          @clear-clicked="clearProjectsAndMailboxes"
        >
          <template #value-display>
            <template v-if="selectedSourceNames.length === 1">
              {{ selectedSourceNames[0] }}
            </template>
            <template v-else-if="selectedSourceNames.length > 1">
              {{ selectedSourceNames.length }} selected
            </template>
          </template>
        </base-ifp-filter>
        <template #content>
          <div>
            <ul v-if="projectOptions.length">
              <li class="calendar-filter-bar__section-header">Projects</li>
              <li
                v-for="project in projectOptions"
                :key="'project-' + project.id"
                @click="toggleSelection('projectIds', project.id)"
              >
                <span :class="{ 'selected': isProjectSelected(project.id) }">
                  <i class="ddi ddi-project" /> {{ project.name }}
                </span>
              </li>
            </ul>
            <hr v-if="projectOptions.length && mailboxOptions.length">
            <ul v-if="mailboxOptions.length">
              <li class="calendar-filter-bar__section-header">Mailboxes</li>
              <li
                v-for="mailbox in mailboxOptions"
                :key="'mailbox-' + mailbox.id"
                @click="toggleSelection('mailboxIds', mailbox.id)"
              >
                <span :class="{ 'selected': isMailboxSelected(mailbox.id) }">
                  <i class="ddi ddi-mailbox" /> {{ mailbox.name }}
                </span>
              </li>
            </ul>
          </div>
        </template>
      </dropdown>

      <!-- Assignees -->
      <dropdown title="Assignees" popper-min-width-size="large">
        <base-ifp-filter
          label="Assignee"
          clearable
          @clear-clicked="clearFilter('assigneeIds')"
        >
          <template #value-display>
            <template v-if="selectedAssigneeNames.length === 1">
              {{ selectedAssigneeNames[0] }}
            </template>
            <template v-else-if="selectedAssigneeNames.length > 1">
              {{ selectedAssigneeNames.length }} selected
            </template>
          </template>
        </base-ifp-filter>
        <template #content>
          <div>
            <ul>
              <li
                v-for="assignee in assigneeOptions"
                :key="assignee.id"
                @click="toggleSelection('assigneeIds', assignee.id)"
              >
                <avatar :person="assignee" :size="24" />
                <span :class="{ 'selected': isAssigneeSelected(assignee.id) }">
                  {{ assignee.name }}
                </span>
              </li>
            </ul>
          </div>
        </template>
      </dropdown>

      <!-- Statuses -->
      <ifp-filter-statuses
        :selected-ids="selectedStatusIds"
        :status-type="selectedStatusType"
        :active-status-options="activeStatusOptions"
        :inactive-status-options="inactiveStatusOptions"
        @selected-ids-updated="onStatusIdsUpdated"
        @status-type-updated="onStatusTypeUpdated"
      />

      <!-- Priorities -->
      <ifp-filter-priorities
        :selected-ids="selectedPriorityIds"
        :priority-options="priorityOptions"
        @selected-ids-updated="onPriorityIdsUpdated"
      />

      <!-- Clear filters -->
      <dd-button
        v-if="hasActiveFilters"
        type="text"
        class="clear-filters-button"
        @click="$emit('reset-filters')"
      >
        Clear
      </dd-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'
import IfpFilterStatuses from '@/components/listing/item-filters-panel/filters/ifp-filter-statuses.vue'
import IfpFilterPriorities from '@/components/listing/item-filters-panel/filters/ifp-filter-priorities.vue'
import {
  IPersonWithEmail,
  IStatusForSelection,
  IPriorityLevelForSelection,
  IProjectForSelection,
} from '@/api/types/response.types'

export default defineComponent({
  name: 'CalendarFilterBar',

  components: {
    IfpFilterStatuses,
    IfpFilterPriorities,
  },

  props: {
    projectOptions: {
      type: Array as () => IProjectForSelection[],
      default: () => [] as IProjectForSelection[],
    },
    mailboxOptions: {
      type: Array as () => IProjectForSelection[],
      default: () => [] as IProjectForSelection[],
    },
    assigneeOptions: {
      type: Array as () => IPersonWithEmail[],
      default: () => [] as IPersonWithEmail[],
    },
    activeStatusOptions: {
      type: Array as () => IStatusForSelection[],
      default: () => [] as IStatusForSelection[],
    },
    inactiveStatusOptions: {
      type: Array as () => IStatusForSelection[],
      default: () => [] as IStatusForSelection[],
    },
    selectedStatusType: {
      type: String,
      default: '',
    },
    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      default: () => [] as IPriorityLevelForSelection[],
    },
    selectedProjectIds: {
      type: Array as () => number[],
      default: () => [] as number[],
    },
    selectedMailboxIds: {
      type: Array as () => number[],
      default: () => [] as number[],
    },
    selectedAssigneeIds: {
      type: Array as () => number[],
      default: () => [] as number[],
    },
    selectedStatusIds: {
      type: Array as () => number[],
      default: () => [] as number[],
    },
    selectedPriorityIds: {
      type: Array as () => number[],
      default: () => [] as number[],
    },
  },

  emits: [
    'reset-filters',
    'update:',
    'update:selectedAssigneeIds',
    'update:selectedMailboxIds',
    'update:selectedPriorityIds',
    'update:selectedProjectIds',
    'update:selectedStatusIds',
    'update:selectedStatusType',
  ],

  data() {
    return {
      internalProjectIds: [] as number[],
      internalMailboxIds: [] as number[],
      internalAssigneeIds: [] as number[],
      debouncedEmitters: {} as Record<string, () => void>,
    }
  },

  computed: {
    hasActiveFilters(): boolean {
      return (
        this.internalProjectIds.length > 0 ||
        this.internalMailboxIds.length > 0 ||
        this.internalAssigneeIds.length > 0 ||
        this.selectedStatusIds.length > 0 ||
        this.selectedStatusType !== '' ||
        this.selectedPriorityIds.length > 0
      )
    },
    selectedSourceNames(): string[] {
      const projectNames = this.projectOptions
        .filter(p => this.internalProjectIds.includes(p.id))
        .map(p => p.name)
      const mailboxNames = this.mailboxOptions
        .filter(m => this.internalMailboxIds.includes(m.id))
        .map(m => m.name)
      return [...projectNames, ...mailboxNames]
    },
    selectedAssigneeNames(): string[] {
      return this.assigneeOptions
        .filter(a => this.internalAssigneeIds.includes(a.id))
        .map(a => a.name)
    },
  },

  watch: {
    selectedProjectIds: {
      deep: 1,
      immediate: true,
      handler(val: number[]) {
        this.internalProjectIds = val
      },
    },
    selectedMailboxIds: {
      deep: 1,
      immediate: true,
      handler(val: number[]) {
        this.internalMailboxIds = val
      },
    },
    selectedAssigneeIds: {
      deep: 1,
      immediate: true,
      handler(val: number[]) {
        this.internalAssigneeIds = val
      },
    },
  },

  created() {
    const emitDelay = 500
    this.debouncedEmitters = {
      projectIds: debounce(() => {
        this.$emit('update:selectedProjectIds', this.internalProjectIds)
      }, emitDelay),
      mailboxIds: debounce(() => {
        this.$emit('update:selectedMailboxIds', this.internalMailboxIds)
      }, emitDelay),
      assigneeIds: debounce(() => {
        this.$emit('update:selectedAssigneeIds', this.internalAssigneeIds)
      }, emitDelay),
    }
  },

  methods: {
    isProjectSelected(id: number): boolean {
      return this.internalProjectIds.includes(id)
    },
    isMailboxSelected(id: number): boolean {
      return this.internalMailboxIds.includes(id)
    },
    isAssigneeSelected(id: number): boolean {
      return this.internalAssigneeIds.includes(id)
    },
    toggleSelection(filterKey: string, id: number) {
      const internalKey = ('internal' +
        filterKey.charAt(0).toUpperCase() +
        filterKey.slice(1)) as
        | 'internalProjectIds'
        | 'internalMailboxIds'
        | 'internalAssigneeIds'
      const current = this[internalKey] as number[]
      if (current.includes(id)) {
        ;(this as any)[internalKey] = current.filter(i => i !== id)
      } else {
        ;(this as any)[internalKey] = [...current, id]
      }
      this.debouncedEmitters[filterKey]()
    },

    clearFilter(filterKey: string) {
      const internalKey = ('internal' +
        filterKey.charAt(0).toUpperCase() +
        filterKey.slice(1)) as
        | 'internalProjectIds'
        | 'internalMailboxIds'
        | 'internalAssigneeIds'
      ;(this as any)[internalKey] = []
      this.emitImmediately(filterKey)
    },

    clearProjectsAndMailboxes() {
      this.internalProjectIds = []
      this.internalMailboxIds = []
      this.emitImmediately('projectIds')
      this.emitImmediately('mailboxIds')
    },

    onStatusIdsUpdated(ids: number[]) {
      this.$emit('update:selectedStatusIds', ids)
    },

    onStatusTypeUpdated(type: string) {
      this.$emit('update:selectedStatusType', type)
    },

    onPriorityIdsUpdated(ids: number[]) {
      this.$emit('update:selectedPriorityIds', ids)
    },

    emitImmediately(filterKey: string) {
      const internalKey =
        'internal' + filterKey.charAt(0).toUpperCase() + filterKey.slice(1)
      const propKey =
        'selected' + filterKey.charAt(0).toUpperCase() + filterKey.slice(1)
      ;(this.$emit as (event: string, ...args: unknown[]) => void)('update:' + propKey, (this as any)[internalKey])
    },
  },
})
</script>

<style lang="scss">
.calendar-filter-bar {
  position: relative;
  display: flex;
  align-items: top;
  justify-content: space-between;

  @include media-breakpoint-down(sm) {
    padding: 0 $detail-gutter-pad-sm;
  }

  &__filters-wrapper {
    display: flex;
    align-items: center;
    @include flex-row-horizontal-spacing(7px);
    @include flex-row-vertical-spacing-on-wrap(8px);

    @include media-breakpoint-down(sm) {
      @include flex-row-horizontal-spacing(4px);
      @include flex-row-vertical-spacing-on-wrap(8px);
    }
  }

  &__section-header {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--color-text-third);
    padding: 6px 12px 2px;
    cursor: default;
    pointer-events: none;
  }

  > .clear-filters-button {
    margin-left: 4px;
    color: var(--color-text-third);
    text-decoration: underline;
    height: auto;
    padding: 0;
  }
}
</style>
